import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useBooking } from '../contexts/BookingContext';
import { useCart } from '../contexts/CartContext';
import {
  X, ChevronLeft, ChevronRight, Clock, Calendar as CalendarIcon,
  Check, User, AlertCircle, ShoppingBag,
  Banknote, CreditCard, Wallet, Info,
  MapPin, Car, Store, Plus,
} from 'lucide-react';

const BookingCalendarModal = ({ professional, service, onClose }) => {
  const navigate = useNavigate();
  const { user, userData } = useAuth();
  const { addBooking } = useBooking();
  const { openCart } = useCart();

  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTime, setSelectedTime] = useState(null);
  const [notes, setNotes] = useState('');
  const [success, setSuccess] = useState(false);

  // Client choices
  const [selectedOptionId, setSelectedOptionId] = useState(null);
  const [locationMode, setLocationMode] = useState(null);
  const [serviceAddress, setServiceAddress] = useState('');

  // Add-ons
  const [selectedAddons, setSelectedAddons] = useState([]);

  // Auto-select first option and seed add-ons from `_selectedAddons` (passed from ItemDetailModal)
  React.useEffect(() => {
    if (service?.options?.length > 0 && !selectedOptionId) {
      setSelectedOptionId(service.options[0].id);
    }
    if (Array.isArray(service?._selectedAddons) && selectedAddons.length === 0) {
      setSelectedAddons(service._selectedAddons);
    }
  }, [service?.id]);

  if (!professional || !service) return null;

  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  const timeSlots = [
    '09:00', '09:30', '10:00', '10:30', '11:00', '11:30',
    '12:00', '12:30', '13:00', '13:30', '14:00', '14:30',
    '15:00', '15:30', '16:00', '16:30', '17:00', '17:30'
  ];

  // ============================================================
  // Service options
  // ============================================================
  const hasOptions = Array.isArray(service.options) && service.options.length > 0;
  const selectedOption = hasOptions
    ? service.options.find(o => o.id === selectedOptionId)
    : null;

  // ============================================================
  // Add-ons
  // ============================================================
  const hasAddons = Array.isArray(service.addons) && service.addons.length > 0;

  const toggleAddon = (addon) => {
    setSelectedAddons(prev => {
      const exists = prev.find(a => a.id === addon.id);
      if (exists) return prev.filter(a => a.id !== addon.id);
      return [...prev, addon];
    });
  };

  const isAddonSelected = (addon) =>
    selectedAddons.some(a => a.id === addon.id);

  const addonsTotal = selectedAddons.reduce(
    (sum, a) => sum + Number(a.price || 0),
    0
  );

  // ============================================================
  // Location mode
  // ============================================================
  const serviceLocationMode = service.locationMode || 'salon';
  const showLocationPicker = serviceLocationMode === 'both';

  const effectiveLocationMode = serviceLocationMode === 'both'
    ? (locationMode || 'salon')
    : serviceLocationMode;

  const isMobile = effectiveLocationMode === 'mobile';
  const transportFee = isMobile ? Number(service.transportFee || 0) : 0;

  // ============================================================
  // Effective pricing
  // ============================================================
  const basePrice = hasOptions && selectedOption
    ? Number(selectedOption.price || 0)
    : Number(service.price || 0);

  const effectiveDuration = hasOptions && selectedOption
    ? (selectedOption.duration || service.duration || '45 min')
    : (service.duration || '45 min');

  const subtotal = basePrice + addonsTotal + transportFee;

  // ============================================================
  // Payment policy resolution
  // ============================================================
  const resolvePolicy = () => {
    const rawPolicy = service.paymentPolicy || 'inherit';

    if (rawPolicy === 'inherit') {
      const proDefault = professional.defaultPaymentPolicy || 'after';
      const proDepositPct = professional.defaultDepositPercent || 30;
      return { policy: proDefault, depositPercent: proDepositPct };
    }

    return {
      policy: rawPolicy,
      depositPercent: service.depositPercent || 30,
    };
  };

  const { policy, depositPercent } = resolvePolicy();
  const depositAmount = policy === 'deposit'
    ? Math.round(subtotal * (depositPercent / 100))
    : 0;
  const balance = subtotal - depositAmount;

  // ============================================================
  // Validation
  // ============================================================
  const needsOptionPick = hasOptions && !selectedOption;
  const needsLocationPick = showLocationPicker && !locationMode;
  const needsAddress = isMobile && !serviceAddress.trim();

  const canSubmit =
    selectedDate &&
    selectedTime &&
    !needsOptionPick &&
    !needsLocationPick &&
    !needsAddress;

  // ============================================================
  // Calendar helpers
  // ============================================================
  const getDaysInMonth = () => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const firstDay = new Date(year, month, 1).getDay();
    const daysInMonth = new Date(year, month + 1, 0).getDate();

    const days = [];
    // Sunday-first grid: firstDay is 0 (Sun) – 6 (Sat).
    for (let i = 0; i < firstDay; i++) {
      days.push({ day: null, date: null, isPast: false });
    }

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    for (let d = 1; d <= daysInMonth; d++) {
      const date = new Date(year, month, d);
      const isPast = date < today;
      days.push({
        day: d,
        date,
        isPast,
        isToday: date.toDateString() === today.toDateString(),
      });
    }
    return days;
  };

  const days = getDaysInMonth();

  const prevMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1));
  };
  const nextMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1));
  };

  const handleDateSelect = (day) => {
    if (day.isPast || !day.date) return;
    setSelectedDate(day.date);
    setSelectedTime(null);
  };

  // ============================================================
  // Add to bag
  // ============================================================
  const handleAddToBag = () => {
    if (!canSubmit) {
      alert('Please complete all steps first');
      return;
    }
    if (!user) {
      navigate('/login');
      return;
    }

    addBooking({
      // Client info
      clientName: userData?.name || user.displayName || 'Client',
      clientEmail: userData?.email || user.email || '',
      clientPhone: userData?.phone || '',
      clientImage: userData?.profileImage || user.photoURL || null,

      // Pro info
      professionalId: professional.id,
      professionalName: professional.businessName || professional.name || 'Professional',
      professionalImage: professional.profileImage || professional.coverImage || null,

      // Service info
      serviceId: service.id,
      serviceTitle: service.title || service.name || 'Service',
      serviceName: service.title || service.name || 'Service',
      price: basePrice,
      servicePrice: basePrice,
      optionPrice: hasOptions && selectedOption ? Number(selectedOption.price) : undefined,
      optionId: selectedOption?.id || null,
      optionName: selectedOption?.name || null,
      optionDuration: selectedOption?.duration || null,
      duration: effectiveDuration,
      serviceDuration: effectiveDuration,

      // Add-ons
      addons: selectedAddons.map(a => ({
        id: a.id,
        name: a.name,
        price: Number(a.price || 0),
      })),
      addonsTotal,

      // Location
      locationMode: effectiveLocationMode,
      serviceAddress: isMobile ? serviceAddress.trim() : '',
      transportFee,
      salonAddress: professional.location || '',

      // Booking details
      date: selectedDate.toISOString().split('T')[0],
      time: selectedTime,
      notes: notes.trim(),
      location: isMobile
        ? serviceAddress.trim()
        : (professional.location || ''),

      // Total
      totalPrice: subtotal,

      // Payment policy snapshot
      paymentPolicy: policy,
      depositPercent,
    });

    setSuccess(true);
  };

  const formatPrice = (p) => `R${Number(p || 0).toFixed(2)}`;

  // ============================================================
  // Payment copy
  // ============================================================
  const getPaymentCopy = (detailed = false) => {
    if (policy === 'after') {
      return {
        icon: <Banknote size={16} className="text-[#AF8BD4] shrink-0" />,
        title: 'Pay after service',
        line: detailed
          ? `No payment now. ${formatPrice(subtotal)} due at your appointment.`
          : `${formatPrice(subtotal)} due at appointment`,
      };
    }
    if (policy === 'full') {
      return {
        icon: <Wallet size={16} className="text-[#AF8BD4] shrink-0" />,
        title: 'Full payment upfront',
        line: detailed
          ? `${formatPrice(subtotal)} required before your appointment.`
          : `${formatPrice(subtotal)} to secure booking`,
      };
    }
    return {
      icon: <CreditCard size={16} className="text-[#AF8BD4] shrink-0" />,
      title: 'Deposit required',
      line: detailed
        ? `${formatPrice(depositAmount)} deposit now, ${formatPrice(balance)} at appointment.`
        : `${formatPrice(depositAmount)} now · ${formatPrice(balance)} later`,
    };
  };

  const paymentCopy = getPaymentCopy();

  // ============================================================
  // SUCCESS SCREEN
  // ============================================================
  if (success) {
    return (
      <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/50">
        <div className="bg-white rounded-3xl w-full max-w-md p-6 sm:p-8 text-center shadow-2xl">
          <div className="w-20 h-20 bg-[#F4EEFF] rounded-full flex items-center justify-center mx-auto mb-4">
            <div className="w-14 h-14 bg-[#AF8BD4] rounded-full flex items-center justify-center">
              <ShoppingBag size={26} className="text-white" />
            </div>
          </div>
          <h2 className="text-2xl font-serif font-bold text-gray-900 mb-2">Added to Your Bag</h2>
          <p className="text-gray-500 text-sm mb-4">
            Your booking request is saved. Open your bag to review and send it to {professional.businessName || professional.name}.
          </p>

          <div className="bg-[#FCF8F9] rounded-2xl p-4 text-left text-sm mb-4 space-y-2">
            <div className="flex justify-between gap-3">
              <span className="text-gray-500 shrink-0">Service</span>
              <span className="font-bold text-gray-900 truncate">
                {service.title || service.name}
              </span>
            </div>
            {selectedOption && (
              <div className="flex justify-between gap-3">
                <span className="text-gray-500 shrink-0">Option</span>
                <span className="font-bold text-gray-900 truncate">{selectedOption.name}</span>
              </div>
            )}
            {selectedAddons.length > 0 && (
              <div className="flex justify-between gap-3">
                <span className="text-gray-500 shrink-0">Add-ons</span>
                <span className="font-bold text-gray-900 truncate text-right">
                  {selectedAddons.map(a => a.name).join(', ')}
                </span>
              </div>
            )}
            {showLocationPicker && (
              <div className="flex justify-between gap-3">
                <span className="text-gray-500 shrink-0">Location</span>
                <span className="font-bold text-gray-900 truncate">
                  {effectiveLocationMode === 'mobile' ? 'Pro comes to me' : 'I go to pro'}
                </span>
              </div>
            )}
            <div className="flex justify-between">
              <span className="text-gray-500">Date</span>
              <span className="font-bold text-gray-900">
                {selectedDate?.toLocaleDateString('en-ZA', { weekday: 'short', month: 'short', day: 'numeric' })}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">Time</span>
              <span className="font-bold text-gray-900">{selectedTime}</span>
            </div>
          </div>

          <div className="bg-[#F4EEFF] rounded-2xl p-3 text-left mb-5">
            <div className="flex items-start gap-2">
              {paymentCopy.icon}
              <div className="flex-1 min-w-0">
                <p className="text-[11px] font-bold text-[#AF8BD4] uppercase tracking-wider">
                  {paymentCopy.title}
                </p>
                <p className="text-[12px] text-gray-700 mt-0.5">
                  {getPaymentCopy(true).line}
                </p>
              </div>
            </div>
          </div>

          <div className="flex gap-3">
            <button
              onClick={onClose}
              className="flex-1 py-3 rounded-full bg-[#FCF8F9] text-gray-700 font-bold text-sm hover:bg-gray-100 transition-colors"
            >
              Keep Browsing
            </button>
            <button
              onClick={() => {
                onClose();
                openCart('bookings');
              }}
              className="flex-1 py-3 rounded-full bg-[#010101] text-white font-bold text-sm hover:bg-black/90 transition-colors"
            >
              View Cart
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ============================================================
  // BOOKING FORM
  // ============================================================
  return (
    <div className="fixed inset-0 z-[60] flex items-stretch sm:items-center justify-center sm:p-4 bg-black/50">
      <div className="bg-white rounded-none sm:rounded-3xl w-full sm:max-w-lg h-full sm:h-auto sm:max-h-[95vh] overflow-hidden flex flex-col shadow-2xl">

        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-50 shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-[#FFF5F6] flex items-center justify-center text-[#EFA9B6]">
              <CalendarIcon size={20} />
            </div>
            <div>
              <h2 className="font-serif font-bold text-lg text-gray-900">Book Appointment</h2>
              <p className="text-xs text-gray-400">Complete the steps below</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-10 h-10 rounded-full bg-[#FCF8F9] flex items-center justify-center hover:bg-gray-100 transition-colors"
          >
            <X size={20} />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-5 hide-scrollbar">

          {/* Summary */}
          <div className="bg-[#FCF8F9] rounded-2xl p-4 mb-5 border border-gray-50">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-full bg-[#FFF5F6] flex items-center justify-center shrink-0 overflow-hidden">
                {professional.profileImage || professional.coverImage ? (
                  <img
                    src={professional.profileImage || professional.coverImage}
                    alt={professional.businessName || professional.name}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <User size={20} className="text-[#EFA9B6]" />
                )}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-xs text-gray-400 font-medium">
                  {professional.businessName || professional.name}
                </p>
                <p className="font-bold text-gray-900 text-sm truncate">
                  {service.title || service.name}
                  {selectedOption && <span className="text-[#AF8BD4]"> · {selectedOption.name}</span>}
                </p>
              </div>
              <div className="text-right shrink-0">
                <p className="font-bold text-gray-900">{formatPrice(subtotal)}</p>
                <p className="text-xs text-gray-400 flex items-center gap-1 justify-end">
                  <Clock size={10} /> {effectiveDuration}
                </p>
              </div>
            </div>
          </div>

          {/* ============================================ */}
          {/* STEP 1 — LOCATION */}
          {/* ============================================ */}
          {showLocationPicker && (
            <div className="mb-5">
              <div className="flex items-center gap-2 mb-3">
                <MapPin size={14} className="text-[#AF8BD4]" />
                <h3 className="font-serif font-bold text-gray-900 text-sm">Where?</h3>
                <span className="text-[10px] font-bold text-[#AF8BD4] uppercase tracking-wider">Required</span>
              </div>

              <div className="grid grid-cols-2 gap-2">
                <button
                  type="button"
                  onClick={() => setLocationMode('salon')}
                  className={`p-3 rounded-2xl border text-left transition-colors ${
                    effectiveLocationMode === 'salon' && locationMode === 'salon'
                      ? 'border-[#EFA9B6] bg-[#FFF5F6]'
                      : 'border-gray-100 hover:border-[#FBE1E8]'
                  }`}
                >
                  <Store size={16} className="text-[#EFA9B6] mb-2" />
                  <p className="font-bold text-xs text-gray-900">I go to pro</p>
                  <p className="text-[10px] text-gray-500 mt-0.5 truncate">
                    {professional.location || 'At their studio'}
                  </p>
                </button>

                <button
                  type="button"
                  onClick={() => setLocationMode('mobile')}
                  className={`p-3 rounded-2xl border text-left transition-colors ${
                    locationMode === 'mobile'
                      ? 'border-[#EFA9B6] bg-[#FFF5F6]'
                      : 'border-gray-100 hover:border-[#FBE1E8]'
                  }`}
                >
                  <Car size={16} className="text-[#EFA9B6] mb-2" />
                  <p className="font-bold text-xs text-gray-900">Pro comes to me</p>
                  {service.transportFee > 0 && (
                    <p className="text-[10px] text-[#AF8BD4] font-bold mt-0.5">
                      +{formatPrice(service.transportFee)}
                    </p>
                  )}
                </button>
              </div>

              {locationMode === 'mobile' && (
                <div className="mt-3">
                  <label className="text-[10px] font-bold text-gray-500 uppercase tracking-[0.15em] block mb-1.5">
                    Your address *
                  </label>
                  <input
                    type="text"
                    value={serviceAddress}
                    onChange={(e) => setServiceAddress(e.target.value)}
                    placeholder="e.g. 42 Long Street, Cape Town CBD"
                    className="w-full bg-[#FCF8F9] border border-transparent rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-[#EFA9B6] focus:bg-white transition-colors"
                  />
                </div>
              )}
            </div>
          )}

          {/* ============================================ */}
          {/* STEP 2 — SERVICE OPTION */}
          {/* ============================================ */}
          {hasOptions && (
            <div className="mb-5">
              <div className="flex items-center gap-2 mb-3">
                <Check size={14} className="text-[#AF8BD4]" />
                <h3 className="font-serif font-bold text-gray-900 text-sm">Choose an option</h3>
                <span className="text-[10px] font-bold text-[#AF8BD4] uppercase tracking-wider">Required</span>
              </div>

              <div className="space-y-2">
                {service.options.map((opt) => {
                  const isSelected = selectedOptionId === opt.id;
                  return (
                    <button
                      key={opt.id}
                      type="button"
                      onClick={() => setSelectedOptionId(opt.id)}
                      className={`w-full text-left p-3 rounded-2xl border transition-colors flex items-center gap-3 ${
                        isSelected
                          ? 'border-[#EFA9B6] bg-[#FFF5F6]'
                          : 'border-gray-100 hover:border-[#FBE1E8]'
                      }`}
                    >
                      <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center shrink-0 ${
                        isSelected ? 'border-[#EFA9B6] bg-[#EFA9B6]' : 'border-gray-300'
                      }`}>
                        {isSelected && <Check size={12} className="text-white" strokeWidth={3} />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-bold text-sm text-gray-900 truncate">{opt.name}</p>
                        <div className="flex items-center gap-2 text-[11px] text-gray-500 mt-0.5">
                          <Clock size={10} />
                          <span>{opt.duration || effectiveDuration}</span>
                        </div>
                      </div>
                      <span className="font-bold text-gray-900 text-sm shrink-0">
                        {formatPrice(opt.price)}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* ============================================ */}
          {/* STEP 2b — ADD-ONS */}
          {/* ============================================ */}
          {hasAddons && (
            <div className="mb-5">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <Plus size={14} className="text-[#AF8BD4]" />
                  <h3 className="font-serif font-bold text-gray-900 text-sm">Add-ons</h3>
                </div>
                <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">
                  Optional
                </span>
              </div>

              <div className="space-y-2">
                {service.addons.map((addon) => {
                  const isSelected = isAddonSelected(addon);
                  return (
                    <button
                      key={addon.id}
                      type="button"
                      onClick={() => toggleAddon(addon)}
                      className={`w-full text-left p-3 rounded-2xl border transition-colors flex items-center gap-3 ${
                        isSelected
                          ? 'border-[#AF8BD4] bg-[#F4EEFF]'
                          : 'border-gray-100 hover:border-[#E0D4F5]'
                      }`}
                    >
                      <div className={`w-5 h-5 rounded-md border-2 flex items-center justify-center shrink-0 ${
                        isSelected ? 'border-[#AF8BD4] bg-[#AF8BD4]' : 'border-gray-300'
                      }`}>
                        {isSelected && <Check size={12} className="text-white" strokeWidth={3} />}
                      </div>
                      <p className="flex-1 min-w-0 font-bold text-sm text-gray-900 truncate">
                        {addon.name}
                      </p>
                      <span className={`font-bold text-sm shrink-0 ${
                        isSelected ? 'text-[#AF8BD4]' : 'text-gray-900'
                      }`}>
                        +{formatPrice(addon.price)}
                      </span>
                    </button>
                  );
                })}
              </div>

              {selectedAddons.length > 0 && (
                <div className="mt-2 text-[11px] text-[#AF8BD4] font-bold text-right">
                  Add-ons total: +{formatPrice(addonsTotal)}
                </div>
              )}
            </div>
          )}

          {/* ============================================ */}
          {/* STEP 3 — DATE */}
          {/* ============================================ */}
          <div className="mb-5">
            <div className="flex items-center gap-2 mb-3">
              <CalendarIcon size={14} className="text-[#AF8BD4]" />
              <h3 className="font-serif font-bold text-gray-900 text-sm">Pick a date</h3>
              <span className="text-[10px] font-bold text-[#AF8BD4] uppercase tracking-wider">Required</span>
            </div>

            <div className="bg-white border border-gray-100 rounded-2xl p-4">
              <div className="flex items-center justify-between mb-4">
                <button
                  onClick={prevMonth}
                  className="w-8 h-8 rounded-full bg-[#FCF8F9] flex items-center justify-center hover:bg-gray-100 transition-colors"
                >
                  <ChevronLeft size={16} />
                </button>
                <h3 className="font-serif font-bold text-gray-900">
                  {monthNames[currentDate.getMonth()]} {currentDate.getFullYear()}
                </h3>
                <button
                  onClick={nextMonth}
                  className="w-8 h-8 rounded-full bg-[#FCF8F9] flex items-center justify-center hover:bg-gray-100 transition-colors"
                >
                  <ChevronRight size={16} />
                </button>
              </div>

              <div className="grid grid-cols-7 gap-1 mb-2">
                {dayNames.map((day) => (
                  <div key={day} className="text-center text-[10px] font-bold text-gray-400 uppercase py-1">
                    {day}
                  </div>
                ))}
              </div>

              <div className="grid grid-cols-7 gap-0.5 sm:gap-1">
                {days.map((day, idx) => {
                  const isSelected = selectedDate &&
                    day.date &&
                    selectedDate.toDateString() === day.date.toDateString();

                  return (
                    <button
                      key={idx}
                      onClick={() => handleDateSelect(day)}
                      disabled={!day.day || day.isPast}
                      className={`aspect-square rounded-full flex items-center justify-center text-sm font-medium transition-all ${
                        !day.day
                          ? 'invisible'
                          : day.isPast
                          ? 'text-gray-200 cursor-not-allowed'
                          : isSelected
                          ? 'bg-[#010101] text-white shadow-md scale-105'
                          : day.isToday
                          ? 'bg-[#FFF5F6] text-[#EFA9B6] font-bold border border-[#FBE1E8] hover:bg-[#FBE1E8]'
                          : 'text-gray-700 hover:bg-gray-50'
                      }`}
                    >
                      <span className="flex items-center justify-center w-full h-full">
                        {day.day}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>

          {/* ============================================ */}
          {/* STEP 4 — TIME + NOTES + PAYMENT */}
          {/* ============================================ */}
          {selectedDate && (
            <div>
              <div className="flex items-center gap-2 mb-3">
                <Clock size={14} className="text-[#AF8BD4]" />
                <h3 className="font-serif font-bold text-gray-900 text-sm">Pick a time</h3>
                <span className="text-[10px] font-bold text-[#AF8BD4] uppercase tracking-wider">Required</span>
              </div>

              <div className="grid grid-cols-3 sm:grid-cols-4 gap-2 mb-5">
                {timeSlots.map((time) => {
                  const isSelected = selectedTime === time;
                  return (
                    <button
                      key={time}
                      onClick={() => setSelectedTime(time)}
                      className={`py-2.5 rounded-xl text-sm font-bold transition-all ${
                        isSelected
                          ? 'bg-[#010101] text-white shadow-md'
                          : 'bg-[#FCF8F9] text-gray-700 border border-gray-100 hover:border-[#EFA9B6] hover:text-[#EFA9B6]'
                      }`}
                    >
                      {time}
                    </button>
                  );
                })}
              </div>

              <div className="mb-5">
                <label className="text-xs font-bold text-gray-700 uppercase tracking-wider block mb-2">
                  Notes (Optional)
                </label>
                <textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Any special requests for your appointment..."
                  rows="3"
                  className="w-full bg-[#FCF8F9] border border-gray-100 rounded-2xl px-4 py-3 text-sm focus:outline-none focus:border-[#EFA9B6] resize-none"
                />
              </div>

              {/* Payment preview */}
              <div className="bg-[#F4EEFF] rounded-2xl p-4 mb-2">
                <div className="flex items-start gap-3">
                  {paymentCopy.icon}
                  <div className="flex-1 min-w-0">
                    <p className="text-[10px] font-bold text-[#AF8BD4] uppercase tracking-[0.15em]">
                      {paymentCopy.title}
                    </p>
                    <p className="text-sm text-gray-800 mt-1 font-medium">
                      {getPaymentCopy(true).line}
                    </p>

                    <div className="mt-3 pt-3 border-t border-[#D9C6EE] space-y-1.5">
                      <div className="flex justify-between text-xs">
                        <span className="text-gray-600">
                          {selectedOption ? `${selectedOption.name}` : 'Service price'}
                        </span>
                        <span className="font-bold text-gray-900">{formatPrice(basePrice)}</span>
                      </div>
                      {selectedAddons.map(a => (
                        <div key={a.id} className="flex justify-between text-xs">
                          <span className="text-gray-600">+ {a.name}</span>
                          <span className="font-bold text-[#AF8BD4]">+{formatPrice(a.price)}</span>
                        </div>
                      ))}
                      {transportFee > 0 && (
                        <div className="flex justify-between text-xs">
                          <span className="text-gray-600">Transport fee</span>
                          <span className="font-bold text-[#AF8BD4]">+{formatPrice(transportFee)}</span>
                        </div>
                      )}
                      {policy === 'deposit' && (
                        <>
                          <div className="flex justify-between text-xs pt-1.5 border-t border-[#D9C6EE]">
                            <span className="text-gray-600">Total</span>
                            <span className="font-bold text-gray-900">{formatPrice(subtotal)}</span>
                          </div>
                          <div className="flex justify-between text-xs">
                            <span className="text-gray-600">Deposit ({depositPercent}%)</span>
                            <span className="font-bold text-[#AF8BD4]">{formatPrice(depositAmount)}</span>
                          </div>
                          <div className="flex justify-between text-xs">
                            <span className="text-gray-600">Balance at appointment</span>
                            <span className="font-bold text-gray-900">{formatPrice(balance)}</span>
                          </div>
                        </>
                      )}
                    </div>

                    <div className="flex items-start gap-1.5 mt-2 text-[10px] text-gray-500">
                      <Info size={10} className="shrink-0 mt-0.5" />
                      <span>
                        {policy === 'after' && 'Payment collected in person at your appointment.'}
                        {policy === 'deposit' && 'Deposit paid after the pro confirms your booking.'}
                        {policy === 'full' && 'Full payment required after the pro confirms your booking.'}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Empty state hint */}
          {!selectedDate && !needsOptionPick && !needsLocationPick && (
            <div className="bg-[#FFF5F6] rounded-2xl p-4 flex items-start gap-3 border border-[#FBE1E8]">
              <AlertCircle size={18} className="text-[#EFA9B6] shrink-0 mt-0.5" />
              <p className="text-xs text-gray-600 leading-relaxed">
                Select a date to see available time slots.
              </p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div
          className="p-4 border-t border-gray-50 bg-white shrink-0"
          style={{ paddingBottom: 'calc(1rem + env(safe-area-inset-bottom))' }}
        >
          <button
            onClick={handleAddToBag}
            disabled={!canSubmit}
            className={`w-full py-4 rounded-full font-bold text-sm transition-all flex items-center justify-center gap-2 ${
              canSubmit
                ? 'bg-[#010101] text-white shadow-lg hover:bg-black/90 active:scale-[0.98]'
                : 'bg-gray-100 text-gray-400 cursor-not-allowed'
            }`}
          >
            <ShoppingBag size={18} />
            {canSubmit
              ? `Add to Bag · ${formatPrice(subtotal)}`
              : needsOptionPick
              ? 'Choose an option first'
              : needsLocationPick
              ? 'Choose a location first'
              : needsAddress
              ? 'Enter your address'
              : 'Select date & time'}
          </button>
        </div>
      </div>

      <style dangerouslySetInnerHTML={{__html: `
        .hide-scrollbar::-webkit-scrollbar { display: none; }
        .hide-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }
      `}} />
    </div>
  );
};

export default BookingCalendarModal;